import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { toast } from 'react-hot-toast';
import { logger } from '@/features/help/utils/logger';
import { Button } from '@/features/ui/components/Button';
import { MailCheck } from 'lucide-react';
import { useHelpStore } from '@/features/help/stores/help.store';

export const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const {
    resendVerificationEmail,
    isLoading: authIsLoading,
    error: authError,
    firebaseUser,
  } = useAuthStore();

  const [isResending, setIsResending] = useState(false);
  const { openContactModal } = useHelpStore();

  const handleResendEmail = async () => {
    setIsResending(true);
    try {
      const success = await resendVerificationEmail();
      if (success && !useAuthStore.getState().error) {
        // Check store error as resend might return true even if already verified (setting an error)
        toast.success('Verification email resent! Please check your inbox (and spam folder).');
      } else if (useAuthStore.getState().error) {
        toast.error(useAuthStore.getState().error || 'Failed to resend verification email.');
      } else {
        // Fallback, though should be covered by store error or success
        toast.error('Could not resend verification email. Please try again later.');
      }
    } catch (error: any) {
      // This catch might not be hit if resendVerificationEmail doesn't re-throw
      // but sets store.error instead. The logic above handles store.error.
      toast.error(error.message || 'An unexpected error occurred.');
      logger.error('VerifyEmailPage', 'Resend email failed unexpectedly', { error });
    } finally {
      setIsResending(false);
    }
  };

  // If user somehow lands here but IS verified, redirect to dashboard
  // Or if firebaseUser becomes null (e.g. logged out), redirect to login
  React.useEffect(() => {
    if (firebaseUser?.emailVerified) {
      logger.info('VerifyEmailPage', 'User email is verified, redirecting to dashboard.');
      navigate('/dashboard');
    } else if (firebaseUser === null && !authIsLoading) {
      // Check !authIsLoading to ensure it's not initial load state
      logger.info('VerifyEmailPage', 'No Firebase user, redirecting to login.');
      navigate('/login');
    }
  }, [firebaseUser, navigate, authIsLoading]);

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 text-white">
      <div className="max-w-md w-full space-y-8 bg-gray-800 p-10 rounded-lg shadow-xl text-center">
        <MailCheck className="mx-auto h-16 w-16 text-blue-500" />
        <h2 className="mt-6 text-3xl font-extrabold">Verify Your Email Address</h2>
        <p className="mt-2 text-gray-300">
          A verification link has been sent to your email address:
          <strong className="text-blue-400"> {firebaseUser?.email || 'your email'}</strong>.
        </p>
        <p className="text-gray-400">
          Please check your inbox (and spam folder) and click the link to activate your account.
        </p>

        {authError && <p className="mt-4 text-sm text-red-400">Error: {authError}</p>}

        <div className="mt-8 space-y-4">
          <Button
            onClick={handleResendEmail}
            disabled={isResending || authIsLoading} // authIsLoading for global state like too_many_requests from store
            className="w-full"
          >
            {isResending ? (
              <span className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Resending...
              </span>
            ) : (
              'Resend Verification Email'
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/login')}
            className="w-full text-gray-700 hover:text-black border-gray-400 hover:bg-gray-200"
          >
            Back to Login
          </Button>
        </div>

        <p className="mt-6 text-xs text-gray-500">
          If you encounter any issues, please{' '}
          <button
            onClick={openContactModal}
            className="underline text-blue-400 hover:text-blue-300 focus:outline-none"
          >
            contact support
          </button>
          .
        </p>
      </div>
    </div>
  );
};
